/**
 * 菜单配置
 * 定义系统的菜单结构，支持二级菜单和分组
 */
import type { MenuItem, MenuGroup } from '@core/types/menu'

// 导出类型供外部使用
export type { MenuItem, MenuGroup }

/**
 * Department permission codes used for menu filtering.
 * Each department account gets their code in interfaceDic.
 * Admin (isAdmin=1) bypasses all checks.
 */
export const DEPT_PERMISSIONS = {
  QC: 'dept:qc',
  EXTERNAL: 'dept:external',
  HR: 'dept:hr',
  SYSTEM: 'dept:system',
  WAREHOUSE: 'dept:warehouse',
  PRODUCTION: 'dept:production',
  EQUIPMENT: 'dept:equipment',
} as const

/**
 * 菜单配置
 * 按照需求分为6个主要分组
 * name = i18n key under "menus.*"
 */
export const menuConfig: MenuItem[] = [
  // 首页（单独显示，不在分组中）
  {
    id: 'dashboard',
    name: 'menus.dashboard',
    path: '/dashboard',
    icon: 'gauge-high',
    order: 0,
    permissions: [],
  },
  {
    id: 'product-journey-tracker',
    name: 'Product Journey Tracker',
    path: '/operations/tracker',
    icon: 'magnifying-glass',
    order: 0,
    permissions: [],
  },

  // 1. 质量管理
  {
    id: 'quality-management',
    name: 'menus.qc',
    icon: 'circle-check',
    order: 1,
    permissions: [DEPT_PERMISSIONS.QC],
    children: [
      {
        id: 'quality-workbench',
        name: 'menus.qcWorkbench',
        path: '/quality/workbench',
        icon: 'gauge-high',
        order: 1,
      },
      {
        id: 'quality-inspection',
        name: 'menus.qcInspection',
        path: '/quality/list',
        icon: 'clipboard-list',
        order: 2,
      },
      {
        id: 'quality-items',
        name: 'menus.qcItems',
        path: '/quality/items',
        icon: 'list-check',
        order: 3,
      },
      {
        id: 'quality-standards',
        name: 'menus.qcStandards',
        path: '/quality/standards',
        icon: 'file',
        order: 4,
      },
      {
        id: 'quality-templates',
        name: 'menus.qcTemplates',
        path: '/quality/templates',
        icon: 'copy',
        order: 5,
      },
      {
        id: 'quality-samples',
        name: 'menus.qcSamples',
        path: '/quality/samples',
        icon: 'layer-group',
        order: 6,
      },
      {
        id: 'quality-nc',
        name: 'menus.qcNc',
        path: '/quality/nc-records',
        icon: 'circle-exclamation',
        order: 7,
      },
    ],
  },

  // 2. 外部信息
  {
    id: 'external-info',
    name: 'menus.external',
    icon: 'link',
    order: 2,
    permissions: [DEPT_PERMISSIONS.EXTERNAL],
    children: [
      {
        id: 'customer-info',
        name: 'menus.externalCustomers',
        path: '/external/customers',
        icon: 'users',
        order: 1,
      },
      {
        id: 'product-info',
        name: 'menus.externalProducts',
        path: '/external/products',
        icon: 'box-open',
        order: 2,
      },
      {
        id: 'order-info',
        name: 'menus.externalOrders',
        path: '/external/orders',
        icon: 'file-lines',
        order: 3,
      },
      {
        id: 'formula-info',
        name: 'menus.externalFormulas',
        path: '/external/formulas',
        icon: 'flask',
        order: 4,
      },
    ],
  },

  // 3. 人力资源
  {
    id: 'human-resources',
    name: 'menus.hr',
    icon: 'users',
    order: 3,
    permissions: [DEPT_PERMISSIONS.HR],
    children: [
      {
        id: 'personnel',
        name: 'menus.hrPersonnel',
        path: '/hr/employees',
        activePaths: ['/hr/personnel'],
        icon: 'user',
        order: 1,
      },
      {
        id: 'shift-management',
        name: 'menus.hrShifts',
        path: '/hr/shifts/setup',
        activePaths: ['/hr/shifts'],
        icon: 'calendar-days',
        order: 2,
      },
      {
        id: 'attendance-report',
        name: 'menus.hrAttendance',
        path: '/hr/attendance',
        icon: 'file-lines',
        order: 3,
      },
      {
        id: 'overtime-management',
        name: 'menus.hrOvertime',
        path: '/hr/overtime',
        icon: 'clock',
        order: 4,
      },
      {
        id: 'leave-management',
        name: 'menus.hrLeave',
        path: '/hr/leave',
        icon: 'right-from-bracket',
        order: 5,
      },
      {
        id: 'performance',
        name: 'menus.hrPerformance',
        path: '/hr/performance',
        icon: 'star',
        order: 6,
      },
      {
        id: 'skill-authorization',
        name: 'menus.hrSkills',
        path: '/hr/skills',
        icon: 'certificate',
        order: 7,
      },
      {
        id: 'org-structure',
        name: 'menus.hrOrgStructure',
        path: '/hr/org-structure',
        icon: 'sitemap',
        order: 8,
      },
      {
        id: 'workforce-overview',
        name: 'menus.hrOverview',
        path: '/hr/overview',
        icon: 'chart-line',
        order: 9,
      },
      {
        id: 'approval-center',
        name: 'menus.hrApprovals',
        path: '/hr/approvals',
        icon: 'check-double',
        order: 9,
      },
    ],
  },

  // 4. 系统设置
  {
    id: 'system-settings',
    name: 'menus.settings',
    icon: 'gear',
    order: 4,
    permissions: [DEPT_PERMISSIONS.SYSTEM],
    children: [
      {
        id: 'menu-management',
        name: 'menus.settingsMenus',
        path: '/settings/menus',
        icon: 'table-cells',
        order: 1,
      },
      {
        id: 'organization',
        name: 'menus.settingsOrganization',
        path: '/settings/organization',
        icon: 'sitemap',
        order: 2,
      },
      {
        id: 'quality-standards-setting',
        name: 'menus.settingsQuality',
        path: '/settings/quality-standards',
        icon: 'circle-check',
        order: 3,
      },
      {
        id: 'process-management',
        name: 'menus.settingsProcesses',
        path: '/settings/processes',
        icon: 'diagram-project',
        order: 4,
      },
      {
        id: 'role-management',
        name: 'menus.settingsRoles',
        path: '/settings/roles',
        icon: 'user-gear',
        order: 5,
      },
      {
        id: 'dict-management',
        name: 'menus.settingsDict',
        path: '/settings/dict',
        icon: 'book',
        order: 6,
      },
      {
        id: 'system-logs',
        name: 'menus.settingsLogs',
        path: '/settings/logs',
        icon: 'file-lines',
        order: 7,
      },
      {
        id: 'system-changelog',
        name: 'menus.settingsChangelog',
        path: '/settings/changelog',
        icon: 'book',
        order: 8,
      },
    ],
  },

  // 5. 仓储管理
  {
    id: 'warehouse-management',
    name: 'menus.warehouse',
    icon: 'warehouse',
    order: 5,
    permissions: [DEPT_PERMISSIONS.WAREHOUSE],
    children: [
      {
        id: 'warehouse-workbench',
        name: 'menus.warehouseWorkbench',
        path: '/warehouse/workbench',
        icon: 'gauge-high',
        order: 1,
      },
      {
        id: 'warehouse-setup',
        name: 'menus.warehouseSetup',
        path: '/warehouse/setup',
        icon: 'gear',
        order: 2,
      },
      {
        id: 'location-manage',
        name: 'menus.warehouseLocations',
        path: '/warehouse/locations',
        icon: 'boxes-stacked',
        order: 3,
      },
      {
        id: 'inventory-ledger',
        name: 'menus.warehouseInventory',
        path: '/warehouse/inventory',
        icon: 'clipboard-list',
        order: 4,
      },
      {
        id: 'semi-finished',
        name: 'menus.warehouseSemiFinished',
        path: '/warehouse/semi-finished',
        icon: 'layer-group',
        order: 5,
      },
      {
        id: 'inbound-outbound',
        name: 'menus.warehouseInOut',
        path: '/warehouse/inout',
        icon: 'right-left',
        order: 6,
      },
      {
        id: 'scan-operation',
        name: 'menus.warehouseScan',
        path: '/warehouse/scan-inbound',
        icon: 'qrcode',
        order: 7,
      },
      {
        id: 'stock-check',
        name: 'menus.warehouseStockCheck',
        path: '/warehouse/stock-check',
        icon: 'circle-check',
        order: 8,
      },
      {
        id: 'batch-trace',
        name: 'menus.warehouseBatchTrace',
        path: '/warehouse/batch-trace',
        icon: 'magnifying-glass',
        order: 9,
      },
      {
        id: 'qrcode-manage',
        name: 'menus.warehouseQrcode',
        path: '/warehouse/qrcode',
        icon: 'qrcode',
        order: 10,
      },
    ],
  },

  // 6. 生产管理
  {
    id: 'production-management',
    name: 'menus.production',
    icon: 'industry',
    order: 6,
    permissions: [DEPT_PERMISSIONS.PRODUCTION],
    children: [
      {
        id: 'production-workbench',
        name: 'menus.productionWorkbench',
        path: '/production/workbench',
        icon: 'gauge-high',
        order: 1,
      },
      {
        id: 'production-schedule-board',
        name: 'menus.productionScheduleBoard',
        path: '/production/schedule-board',
        icon: 'calendar-days',
        order: 2,
      },
      {
        id: 'production-procurement-review',
        name: 'menus.productionProcurementReview',
        path: '/production/procurement-review',
        icon: 'clipboard-list',
        order: 3,
      },
      {
        id: 'production-order',
        name: 'menus.productionPO',
        path: '/production/po',
        icon: 'file-lines',
        order: 2,
      },
      {
        id: 'manufacturing-order',
        name: 'menus.productionMO',
        path: '/production/mo',
        icon: 'list-check',
        order: 3,
      },
      {
        id: 'production-prepare',
        name: 'menus.productionPrepare',
        path: '/production/prepare',
        icon: 'clipboard-list',
        order: 4,
      },
      {
        id: 'operation-report',
        name: 'menus.productionReport',
        path: '/production/operation-report',
        icon: 'pen',
        order: 5,
      },
      {
        id: 'first-piece-confirm',
        name: 'menus.productionFirstPiece',
        path: '/production/first-piece',
        icon: 'circle-check',
        order: 6,
      },
      {
        id: 'exception-report',
        name: 'menus.productionException',
        path: '/production/exception',
        icon: 'circle-exclamation',
        order: 7,
      },
      {
        id: 'completion-inbound',
        name: 'menus.productionCompletion',
        path: '/production/completion',
        icon: 'boxes-packing',
        order: 8,
      },
    ],
  },

  // 7. 设备管理
  {
    id: 'equipment-management',
    name: 'menus.equipment',
    icon: 'server',
    order: 7,
    permissions: [DEPT_PERMISSIONS.EQUIPMENT],
    children: [
      {
        id: 'equipment-ledger',
        name: 'menus.equipmentLedger',
        path: '/equipment/ledger',
        icon: 'list-ul',
        order: 1,
      },
      {
        id: 'equipment-maintenance',
        name: 'menus.equipmentMaintenance',
        path: '/equipment/maintenance',
        icon: 'wrench',
        order: 2,
      },
    ],
  },
]

/**
 * 扁平化菜单（获取所有叶子节点）
 */
export function getFlatMenuItems(menuItems: MenuItem[]): MenuItem[] {
  const result: MenuItem[] = []

  function traverse(items: MenuItem[]) {
    for (const item of items) {
      if (item.children && item.children.length > 0) {
        traverse(item.children)
      } else if (item.path) {
        result.push(item)
      }
    }
  }

  traverse(menuItems)
  return result
}

/**
 * 根据路径查找菜单项
 */
export function findMenuItemByPath(menuItems: MenuItem[], path: string): MenuItem | null {
  for (const item of menuItems) {
    if (isMenuItemPathMatch(item, path)) {
      return item
    }
    if (item.children) {
      const found = findMenuItemByPath(item.children, path)
      if (found) return found
    }
  }
  return null
}

/**
 * 获取菜单面包屑
 */
export function getBreadcrumb(menuItems: MenuItem[], path: string): MenuItem[] {
  const result: MenuItem[] = []

  function traverse(items: MenuItem[], parents: MenuItem[] = []) {
    for (const item of items) {
      const currentPath = [...parents, item]
      if (isMenuItemPathMatch(item, path)) {
        result.push(...currentPath)
        return true
      }
      if (item.children) {
        if (traverse(item.children, currentPath)) {
          return true
        }
      }
    }
    return false
  }

  traverse(menuItems)
  return result
}

function isPathMatch(candidate: string | undefined, currentPath: string): boolean {
  if (!candidate) return false
  return currentPath === candidate || currentPath.startsWith(`${candidate}/`)
}

export function isMenuItemPathMatch(item: MenuItem, currentPath: string): boolean {
  if (isPathMatch(item.path, currentPath)) {
    return true
  }

  return (item.activePaths || []).some((path) => isPathMatch(path, currentPath))
}

/**
 * 根据用户权限过滤菜单
 */
export function filterMenuByPermission(
  menuItems: MenuItem[],
  hasPermission: (p: string) => boolean,
): MenuItem[] {
  return menuItems
    .map((item) => {
      const copy: MenuItem = { ...item }
      if (copy.children) {
        copy.children = filterMenuByPermission(copy.children, hasPermission)
      }
      // 如果定义了权限，则至少要有一个通过
      if (copy.permissions && copy.permissions.length > 0) {
        const ok = copy.permissions.some((p) => hasPermission(p))
        if (!ok) {
          return null
        }
      }
      // 如果没有 path 且没有子菜单，则不可见
      if (!copy.path && (!copy.children || copy.children.length === 0)) {
        return null
      }
      return copy
    })
    .filter((x): x is MenuItem => x !== null)
}
